import {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// App-wide transient toasts.
//
// Deliberately portal-local rather than in @playerbase/ui: there's a single
// consumer (the portal) and the package's promotion rule wants a second
// consumer before anything moves there. The viewport renders each toast with
// the shared Alert atom, so the visual language still comes from the design
// system.
//
// A toast is fire-and-forget: push() returns its id (so a caller can dismiss
// early) and schedules an auto-dismiss. Tones map straight onto Alert's tonal
// variants.
// ---------------------------------------------------------------------------

export type ToastTone = 'info' | 'success' | 'danger';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  // Optional secondary line under the title (e.g. a message count).
  body?: string;
}

export interface ToastInput {
  tone: ToastTone;
  title: string;
  body?: string;
  // Override the default auto-dismiss. Pass null to make the toast sticky
  // (caller must dismiss it). Defaults to DEFAULT_DISMISS_MS.
  dismissAfterMs?: number | null;
}

export interface ToastContextValue {
  toasts: Toast[];
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

// Long enough to read a one-line confirmation, short enough not to pile up.
const DEFAULT_DISMISS_MS = 6_000;

function makeId(): string {
  // crypto.randomUUID is available in every browser the portal targets; the
  // fallback keeps unit tests happy under jsdom shims that omit it.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Track pending timers so dismiss() can clear them and we don't fire a
  // setState after a toast was removed by hand.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string): void => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput): string => {
      const id = makeId();
      const toast: Toast = {
        id,
        tone: input.tone,
        title: input.title,
        ...(input.body !== undefined ? { body: input.body } : {}),
      };
      setToasts((current) => [...current, toast]);

      const ms =
        input.dismissAfterMs === undefined ? DEFAULT_DISMISS_MS : input.dismissAfterMs;
      if (ms !== null) {
        const timer = setTimeout(() => {
          timers.current.delete(id);
          setToasts((current) => current.filter((t) => t.id !== id));
        }, ms);
        timers.current.set(id, timer);
      }
      return id;
    },
    [],
  );

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, push, dismiss }),
    [toasts, push, dismiss],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}
